import { APIManager, axios } from 'shared/manager';
import JSZip, { JSZipObject } from 'jszip';
import Utils from './Utils';
import { areWeTestingWithJest } from 'test/helpers/formik';
import { AxiosRequestConfig } from 'axios';
import FileSaver from 'file-saver';
import Toast from './Toast';

const asyncLinkage = async (url: string): Promise<string> =>
    new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', url, true);
        request.responseType = 'arraybuffer';
        request.onreadystatechange = function () {
            if (request.readyState === 4) {
                if (request.status !== 200) {
                    reject();
                }
            }
        };
        request.onload = async () => {
            const file = btoa(
                new Uint8Array(request.response).reduce(
                    (data, byte) => data + String.fromCharCode(byte),
                    ''
                )
            );

            const responseData = request.getResponseHeader('content-type');
            if (responseData) {
                resolve(`data:${responseData.toLowerCase()};base64,${file}`);
            } else {
                reject();
            }
        };

        request.send();
    });
/**
 * Downloads a file from the provided URL with the specified file name.
 *
 * @param {string} url - The URL of the file to download.
 * @param {string} fileName - The name for the downloaded file.
 * @returns {Promise<void>} A promise that resolves when the file download is initiated.
 */
export const downloadFile = async (
    url: string,
    fileName: string
): Promise<void> => {
    try {
        const linkage = await asyncLinkage(url)
            .then(async (uri) => {
                const link = document.createElement('a');
                link.href = uri;
                link.setAttribute('download', fileName);
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                await new Promise<void>((resolve) => {
                    setTimeout(() => {
                        resolve();
                    }, 1000);
                });
                document.body.removeChild(link);
            })
            .catch(() => false);
        if (linkage === false) {
            throw Error(
                'Unable to process download. Please contact Support for assistance..'
            );
        }
    } catch (error: any) {
        Toast.showErrorMessage(error.message);
    }
};

export const downloadFileSaveAs = async (
    url: string,
    fileName: string
): Promise<void> => {
    try {
        FileSaver.saveAs(url, fileName);
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, 1500);
        });
    } catch (error) {
        Utils.handleErrorResponse(error);
    }
};

/**
 * Downloads a zip from the provided URL with the specified file name.
 *
 * @param {string} url - The URL of the file to download.
 * @returns {Promise<void>} A promise that resolves when the file download is initiated.
 */
export const downloadZip = async (
    remote_url: string
): Promise<{ [key: string]: JSZipObject }> =>
    new Promise((resolve, reject) => {
        const config: AxiosRequestConfig = {
            responseType: 'blob',
        };
        try {
            APIManager.sendGet(remote_url, false, {}, config, axios)
                .then((res) => {
                    if (res.status !== 200 || areWeTestingWithJest()) {
                        // console.log(res.status);
                        // handle error
                        return;
                    }

                    JSZip.loadAsync(res.data, {
                        optimizedBinaryString: true,
                    }).then((zip) => resolve(zip.files));
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });

/**
 * Downloads a file from the provided URL with the specified file name.
 *
 * @param {string} url - The URL of the file to download.
 * @returns {Promise<void>} A promise that resolves when the file download is initiated.
 */
export const downloadPDF = async (remote_url: string): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const config: AxiosRequestConfig = {
            responseType: 'arraybuffer',
        };

        try {
            APIManager.sendGet(remote_url, false, {}, config, axios)
                .then((res) => {
                    if (res.status !== 200 || areWeTestingWithJest()) {
                        // console.log(res.status);
                        // handle error
                        return;
                    }

                    const file = new Blob([res.data], {
                        type: 'application/pdf',
                    });
                    resolve(file);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });

/**
 * Downloads a file from the provided URL with the specified file name.
 *
 * @param {string} url - The URL of the file to download.
 * @returns {Promise<void>} A promise that resolves when the file download is initiated.
 */
export const downloadCSV = async (remote_url: string): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const config: AxiosRequestConfig = {
            responseType: 'arraybuffer',
        };

        try {
            APIManager.sendGet(remote_url, false, {}, config, axios)
                .then((res) => {
                    if (res.status !== 200 || areWeTestingWithJest()) {
                        // console.log(res.status);
                        // handle error
                        return;
                    }

                    const file = new Blob([res.data], {
                        type: 'text/csv;encoding:utf-8',
                    });
                    resolve(file);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });
